import { useCallback } from "react"
import { useReactFlow } from "@xyflow/react"

/**
 * Handles saving the flow diagram to the server and exporting as JSON.
 */
export default function useFlowPersistence(flowSlug, nodes, edges) {
  const reactFlowInstance = useReactFlow()

  // Save handler - persists the full diagram to the server
  const handleSave = useCallback(async () => {
    const viewport = reactFlowInstance.getViewport()
    const diagram = {
      nodes: nodes.map(n => ({
        id: n.id,
        type: n.type,
        position: n.position,
        data: n.data,
        ...(n.parentId && { parentId: n.parentId }),
        ...(n.extent && { extent: n.extent }),
        ...(n.style && { style: n.style }),
      })),
      edges: edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        ...(e.label && { label: e.label }),
        ...(e.sourceHandle && { sourceHandle: e.sourceHandle }),
        ...(e.targetHandle && { targetHandle: e.targetHandle }),
      })),
      viewport,
    }

    try {
      const response = await fetch(`/flows/${flowSlug}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ flow: { diagram } }),
      })

      if (response.ok) {
        const btn = document.querySelector('[data-action="click->react-flow#save"]')
        if (btn) {
          const originalText = btn.textContent
          btn.textContent = "Saved!"
          btn.classList.add("bg-green-600")
          setTimeout(() => {
            btn.textContent = originalText
            btn.classList.remove("bg-green-600")
          }, 1500)
        }
      }
    } catch (error) {
      console.error("Failed to save:", error)
    }
  }, [flowSlug, nodes, edges, reactFlowInstance])

  // Export handler - downloads the diagram as a JSON file
  const handleExport = useCallback(() => {
    const viewport = reactFlowInstance.getViewport()
    const diagram = {
      nodes: nodes.map(n => ({
        id: n.id,
        type: n.type,
        position: n.position,
        data: n.data,
      })),
      edges: edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        label: e.label,
      })),
      viewport,
    }

    const dataStr = JSON.stringify(diagram, null, 2)
    const blob = new Blob([dataStr], { type: "application/json" })
    const url = URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = url
    link.download = `${flowSlug}-flow.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [flowSlug, nodes, edges, reactFlowInstance])

  return { handleSave, handleExport }
}
